<script>
import { componentStore } from "@common/componentStore/componentStore";
import { controlEditorSettings } from "@common/control/controlStore";
import { sDs } from "@common/sDs/sDs";
import { library } from "@common/library/library";

const fontList = [
    { text: 'Proxima Nova', value: 'proxima-nova' },
    { text: 'Open Sans', value: 'Open Sans' },
    { text: 'Source Serif Pro', value: 'Source Serif Pro' },
    { text: 'Schoolbook', value: 'Schoolbook' },
    { text: 'Arial', value: 'Arial' },
    { text: 'Times New Roman', value: 'Times New Roman' },
    { text: 'Courier New', value: 'Courier New' },
    { text: 'Comic Sans MS', value: 'Comic Sans MS' },
    { text: 'Brush Script MT', value: 'Brush Script MT' },
];

const fontWeights = [
    { text: 'Light 300', value: '300' },
    { text: 'Regular 400', value: '400' },
    { text: 'Semi-bold 600', value: '600' },
    { text: 'Bold 700', value: '700' },
    { text: 'Extra-bold 800', value: '800' },
];

const fieldsObj = {
    text: 'text',
    value: 'value'
};

const editorSettings = {
    controlsPanel: {
        type: 'panel',
        label: 'Controls',
        id: 'controlsPanel',
        fields: [
            'controls',
            'syncing',
        ]
    },
    syncing: {
        type: 'object',
        id: 'syncing',
        label: 'Syncing',
        default: {
            profileSyncing: false,
            recordPsp: 'vol',
            recordId: null,
            showSubmitButton: false
        },
        properties: {
            profileSyncing: {
                label: 'Profile syncing',
                type: 'boolean',
                id: 'profileSyncing'
            },
            recordPsp: {
                label: 'Perspective',
                type: 'dropdown',
                id: 'recordPsp',
                fields: { text: 'text', value: 'value' },
                items: [
                    { text: 'Volunteers', value: 'vol' },
                    { text: 'Opportunities', value: 'opp' },
                    { text: 'Organizations', value: 'org' },
                    { text: 'Clients', value: 'mem' },
                    { text: 'Affiliations', value: 'aff' },
                    { text: 'Logbook Entry', value: 'lbe' }
                ],
                default: 'vol',
                logic: [
                    {
                        record: 'self',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: 'this.controls.syncing.value.profileSyncing'
                    }
                ]
            },
            recordSource: {
                label: 'Record Source',
                type: 'dropdown',
                items: [
                    { text: 'Source', value: 'source' },
                    { text: 'Context Record', value: 'context' },
                    { text: 'Fixed Record', value: 'fixed' },
                ],
                fields: {
                    text: 'text',
                    value: 'value'
                },
                default: 'source'
            },
            recordId: {
                label: 'Record ID',
                type: 'number',
                id: 'recordId',
                logic: [
                    {
                        record: 'self',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: 'this.controls.syncing.value.recordSource === "fixed"'
                    }
                ]
            },
            volId: {
                label: 'Volunteer ID Source',
                type: 'dropdown',
                items: [
                    { text: 'Context Volunteer', value: 'context' },
                ],
                fields: {
                    text: 'text',
                    value: 'value',
                },
                id: 'volId',
                logic: [
                    {
                        record: 'self',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: 'this.controls.syncing.value.profileSyncing && this.controls.syncing.value.recordPsp === "lbe"'
                    }
                ]
            },
            surveyId: {
                label: 'Survey ID',
                type: 'number',
                id: 'surveyId',
                logic: [
                    {
                        record: 'self',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: 'this.controls.syncing.value.profileSyncing && this.controls.syncing.value.recordPsp === "lbe"'
                    }
                ]
            },
            showSubmitButton: {
                label: 'Show submit button',
                type: 'boolean',
                id: 'showSubmitButton',
                logic: [
                    {
                        record: 'self',
                        prop: 'display',
                        valueType: 'criteria',
                        criteria: 'this.controls.syncing.value.profileSyncing'
                    }
                ]
            },
        }
    },
    controlPanelSettingsPanel: {
        type: 'panel',
        label: 'Control Panel Settings',
        id: 'controlPanelSettingsPanel',
        fields: [
            'listDirection',
            'listAlignment',
            'customTemplate',
            'controlPanelTitle',
            'controlPanelTitleStyles',
            'customErrorMessage',
            'enableCstPropertySync',
            'enableManualSave',
            'cstPropertySource',
            'syncedCstProperty',
        ]
    },
    controlPanelTitle: {
        type: 'string',
        label: 'Control Panel Title',
        id: 'controlPanelTitle',
        helpText: 'Use this setting to add a header to your control panel.  Leaving it blank will remove the title'
    },
    controlPanelTitleStyles: {
        type: 'object',
        id: 'controlPanelTitleStyles',
        label: 'Control Panel Title Styles',
        helpText: 'Styling for the control panel title',
        default: {
            backgroundColor: 'ffffffff',
            color: '000000ff',
            fontFamily: 'proxima-nova, sans-serif',
            fontSize: '20px',
            fontWeight: '600',
            textAlign: 'center'
        },
        properties: {
            backgroundColor: {
                type: 'color',
                label: 'Header Background Color',
                helpText: 'Color for the header background.',
                id: 'backgroundColor',
                noColor: true
            },
            color: {
                type: 'color',
                label: 'Header Text Color',
                helpText: 'Color for the header text.',
                id: 'color'
            },
            fontSize: {
                type: 'dropdown',
                label: 'Header Text Size',
                helpText: 'Font size for the header text.',
                fields: fieldsObj,
                items: [
                    { text: '16px', value: '16px' },
                    { text: '18px', value: '18px' },
                    { text: '20px', value: '20px' },
                    { text: '24px', value: '24px' },
                    { text: '28px', value: '28px' },
                    { text: '36px', value: '36px' }
                ],
                id: 'headerFontSize',
                default: 'Header Font Size'
            },
            fontFamily: {
                type: 'dropdown',
                label: 'Title Font',
                helpText: 'The font type used for the header text.',
                fields: fieldsObj,
                items: fontList,
                id: 'fontFamily'
            },
            fontWeight: {
                type: 'dropdown',
                label: 'Title Weight',
                helpText: 'The weight of the header text.',
                fields: fieldsObj,
                items: fontWeights,
                id: 'fontWeight'
            },
            textAlign: {
                type: 'dropdown',
                label: 'Text Alignment',
                id: 'textAlign',
                default: 'center',
                helpText: 'Text alignment for the chart title: left, right, center.',
                fields: fieldsObj,
                items: [
                    { text: 'Center', value: 'center' },
                    { text: 'Align Left', value: 'left' },
                    { text: 'Align Right', value: 'right' },
                ]
            },
        }
    },
    listDirection: {
        type: 'dropdown',
        label: 'Control List Direction',
        id: 'listDirection',
        items: [
            'vertical',
            'horizontal'
        ],
        // eslint-disable-next-line max-len
        helpText: 'Controls can be listed vertically or horizontally.  Vertical: puts a single control on every line.  Horizontal: lists the controls across the width of the page following the natural flow of wording.  Vertical is the same as display: block in css, horizontal is the same as display: inline-block in css'
    },
    listAlignment: {
        type: 'dropdown',
        label: 'Alignment',
        id: 'listAlignment',
        options: [
            'left',
            'right',
            'center',
        ],
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.controls.listDirection.value === "horizontal"'
            }
        ],
        default: 'left',
        helpText: 'Align the buttons left right or center of the control panel.'
    },
    customTemplate: {
        type: 'text',
        label: 'Component Template',
        id: 'cTemplate',
        helpText: 'Provide a custom layout using HTML.'
    },
    customErrorMessage: {
        label: 'Custom Error Message',
        type: 'string',
        id: 'customErrorMessage',
        helpText: 'Provide a custom error message if required fields are missing or invalid.'
    },
    enableManualSave: {
        label: 'Enable Save Through Profile Tab Buttons',
        type: 'boolean',
        id: 'enableManualSave',
        default: false,
        helpText: 'Enable Save Through Profile Tab Buttons (e.g. Apply, Finish)'
    },
    enableCstPropertySync: {
        label: 'Enable CST Property Sync',
        type: 'boolean',
        id: 'enableCstPropertySync',
        helpText: 'Sync this control panel to a CST Property. Control values will automatically update based on the configured cstProperty.'
    },
    cstPropertySource: {
        label: 'CST Source',
        type: 'dropdown',
        items: [
            { text: 'Recruiter', value: 'er' },
            { text: 'Coordinator', value: 'ec' },
        ],
        id: 'cstPropertySource',
        helpText: 'Select the source of CST Property.',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.controls.enableCstPropertySync.value'
            }
        ]
    },
    syncedCstProperty: {
        label: 'CST Property',
        type: 'string',
        id: 'syncedCstProperty',
        helpText: 'Type the CST Property you want this control panel to sync with.',
        logic: [
            {
                record: 'self',
                prop: 'display',
                valueType: 'criteria',
                criteria: 'this.controls.enableCstPropertySync.value'
            }
        ]
    }
};
editorSettings.controls = controlEditorSettings.controls;

let controlPanelConfig = {
    cId: "controlPanel",
    cType: "controlPanel",
    cTitle: "Control Panel",
    cTemplate: `<div v-if="config" class="control-panel" :class="configClasses" :style="componentStyles">
        <div class="control-panel-title" v-if="config.controlPanelTitle" :style="config.controlPanelTitleStyles">{{config.controlPanelTitle}}</div>
        <div v-for="iControl in computedControls" class="cp-control-container">
            <control :name="iControl.name" :label="iControl.label" :value="iControl.value || null"></control>
        </div>
        <div v-if="realSyncing.showSubmitButton && recordPsp && !config.enableCstPropertySync && !config.enableManualSave" class="control-panel-actions"><control name="SUBMIT_BUTTON"></control></div>
        <div v-if="config.enableCstPropertySync && !realSyncing.showSubmitButton && !config.enableManualSave" class="control-panel-actions"><control name="SAVE_BUTTON"></control></div>
    </div>`,
    controls: [
        {
            name: 'SAVE_BUTTON',
            label: 'Save',
            type: 'button',
            onclick: 'saveCstProperty'
        },
        {
            name: 'SUBMIT_BUTTON',
            label: 'Submit',
            type: 'button',
            onclick: 'saveProfile'
        }
    ]
};

let controlPanelCmp = {
    name: 'controlPanel',
    editorSettings: editorSettings,
    dataTestIds: [],
    props: {
        record: {
            type: Object,
            default: (() => {})
        },
        syncing: {
            type: Object,
            default: (() => {})
        }
    },
    data() {
        return {
            synced: false
        };
    },
    computed: {
        computedControls() {
            let controls = this.config.controls.map((iControl) => {
                if (iControl.label.indexOf('{{') !== -1 && this.record) {
                    let interpolatedLabel = library.interpolateVueVariables(iControl.label, 'record', this.record);
                    if (interpolatedLabel) iControl.label = interpolatedLabel;
                }
                if (iControl.type === 'upload') {
                    this.controls[iControl.name].psp = this.recordPsp;
                    this.controls[iControl.name].recordId = this.recordId;
                    this.controls[iControl.name].attachIdValue = this.attachIdValue;
                    iControl.psp = this.recordPsp;
                    iControl.recordId = this.recordId;
                    iControl.attachIdValue = iControl.name;
                }
                return iControl;
            });
            if (!this.synced && this.computedControlsNames.length && this.recordPsp && this.recordId && this.recordId > 0) {
                let subscriptionDetail = this.recordStore.subscribe(
                    {
                        source: 'sds_getProfileField',
                        params: {
                            recordId: this.recordId,
                            recordPsp: this.recordPsp,
                            fields: this.computedControlsNames,
                            params: { strictTypeResults: true }
                        },
                        updateFrequency: 0,
                        doNotAddRecords: 1
                    },
                    this.cId
                );
                let tmpValues = this.recordStore.getSubscription(subscriptionDetail.key);
                if (tmpValues && tmpValues[this.recordId]) {
                    this.synced = true;
                    for (let i in controls) {
                        if (typeof tmpValues[this.recordId][controls[i].name] !== 'undefined') {
                            let value = tmpValues[this.recordId][controls[i].name];
                            this.controls[controls[i].name].value = value;
                            controls[i].value = value;
                        }
                    }
                }
            }
            return controls;
        },
        computedControlsNames() {
            let controls = [];
            if (this?.config?.controls) {
                controls = this.config.controls.map((iControl) => iControl.name);
            }
            return controls;
        },
        values() {
            let output = {};
            let controlKeys = Object.keys(this.controls);
            controlKeys.forEach((key) => {
                output[key] = this.controls[key].value;
            });
            return output;
        },
        records() {
            return this.recordStore.records;
        },
        directionClass() {
            if (!this.config.listDirection) return "";
            return 'cp-' + this.config.listDirection;
        },
        alignmentClass() {
            if (!this.config.listAlignment) return "";
            return 'cp-' + this.config.listAlignment;
        },
        configClasses() {
            let classString = '';
            if (this.directionClass) classString += this.directionClass;
            if (this.alignmentClass) classString += ' ' + this.alignmentClass;
            return classString;
        },
        realSyncing() {
            let realSyncing = !library.objIsEmpty(this.syncing) ? this.syncing : this.config.syncing;
            if (typeof realSyncing === 'undefined') realSyncing = {};
            return realSyncing;
        },
        recordPsp() {
            return this.realSyncing.recordPsp;
        },
        recordId() {
            let recordId;
            if (this.realSyncing.recordSource === 'source') {
                if (!this.record) return false;
                recordId = this.record.ID
                    || this.record.uiRecordId
                    || (this.recordPsp === 'vol' && this.record.VOLUNTEER_ID)
                    || (this.recordPsp === 'opp' && this.record.OPPORTUNITY_ID)
                    || (this.recordPsp === 'org' && this.record.ORGANIZATION_ID);
            }
            else if (this.realSyncing.recordSource === 'context') {
                let idKey = this.recordPsp + "Id";
                recordId = this[idKey];
            }
            else if (this.realSyncing.recordSource === 'fixed') recordId = this.realSyncing.recordId;
            return recordId;
        }

    },
    methods: {
        validateFields() {
            // Check validity of field values
            let validationErrors = [];
            for (let i in this.computedControlsNames) {
                let control = this.controls[this.computedControlsNames[i]];
                if (!control) continue;
                if (typeof control.validationErrors === 'object'
                    && control.validationErrors.length) {
                    validationErrors = validationErrors.concat(control.validationErrors);
                }
            }
            return validationErrors;
        },
        manualSave() {
            if (this.config.enableCstPropertySync) {
                return this.saveCstProperty();
            }
            return this.saveProfile();
        },
        async saveProfile() {
            let response = {};
            if (this.realSyncing?.profileSyncing && this.recordPsp) {
                let controlsToSave = {};
                for (let i in this.computedControlsNames) {
                    let tmpControlName = this.computedControlsNames[i];
                    if (
                        this.controls
                        && this.controls[tmpControlName]
                        && !this.controls[tmpControlName].disabled
                    ) {
                        controlsToSave[tmpControlName] = this.controls[tmpControlName].value;
                    }
                }
                if (this.recordId && this.recordId > 0) {
                    let validationErrors = this.validateFields();
                    if (Array.isArray(validationErrors) && validationErrors.length) {
                        // openComponentDialog already validates fields
                        let msg = this.config.customErrorMessage || 'Some fields are invalid.  Please check any fields with an invalid entry.  Note: If the field is not required, you can clear the value to fix the invalid format.';
                        if (!this.contextDialogId) library.showDialog(msg);
                        response.status = 'failure';
                        return response;
                    }
                    if (this.recordPsp === 'aff') response = await actionStore.updateAffiliation({ affID: this.recordId, ...controlsToSave });
                    else response = await actionStore.updateProfileFields({ psp: this.recordPsp, recordId: this.recordId, fields: controlsToSave });
                } else {
                    if (this.recordPsp === 'lbe') {
                        if (this.config.syncing?.volId) {
                            controlsToSave.volId = this.contextVolId;
                        }
                        if (this.config.syncing?.surveyId) {
                            controlsToSave.surveyId = this.config.syncing.surveyId;
                        }
                    }
                    response = await sDs.post('er_create' + library.capitalizeFirstLetter(this.recordPsp), controlsToSave);
                    if (response && response.data && parseInt(response.data, 10) > 0) {
                        this.recordId = parseInt(response.data, 10);
                    }
                }
                this.recordStore.subscribe(
                    {
                        source: 'sds_getProfileField',
                        params: {
                            recordId: this.recordId,
                            recordPsp: this.recordPsp,
                            fields: this.computedControlsNames,
                            params: { strictTypeResults: true }
                        },
                        updateFrequency: 0
                    },
                    this.cId,
                    true
                );
            }
            return response;
        },
        async saveCstProperty() {
            let config = this.config;
            if (!config.enableCstPropertySync || !config.syncedCstProperty || !config.cstPropertySource) return;
            let cstFunc = this.config.cstPropertySource + '_setCstProperty';
            let payload = {};
            this.config.controls.forEach((control) => {
                let name = control.name;
                payload[name] = this.controls[name].value;
            });
            payload = JSON.stringify(payload);
            let response = await sDs.post(cstFunc, this.config.syncedCstProperty, payload);
            let message = response.status === 'success'
                ? 'Your settings have been saved.'
                : 'There was a problem saving your settings.';
            if (!config.enableManualSave) library.showDialog(message, false, false, { showAsAlert: true });
        }
    },
    watch: {
        componentLoading(loading) {
            if (!loading) {
                for (let i in this.controls) {
                    if (i === 'length') continue;
                    let control = this.controls[i];
                    if (control.minDate) control.minDate = library.parseRelativeDate(control.minDate);
                    if (control.maxDate) control.maxDate = library.parseRelativeDate(control.maxDate);
                }
            }
            if (!loading && !this.synced) {
                let config = this.config;
                if (!config.enableCstPropertySync || !config.syncedCstProperty || !config.cstPropertySource) {
                    if (!this.recordId) this.synced = true; // else set synced after recordId is used to get subscription
                    return;
                }
                let cstFunc = this.config.cstPropertySource + '_getCstProperty';
                sDs.post(cstFunc, this.config.syncedCstProperty).then((response) => {
                    if (response && response.status === 'success' && response.data) {
                        let dataObj = JSON.parse(response.data);
                        for (let key in dataObj) {
                            if (key === 'length') continue;
                            if (this.controls[key]) {
                                this.controls[key].value = dataObj[key];
                            }
                        }
                    }
                });
            }
        }
    }
};

componentStore.initComponent(controlPanelCmp, controlPanelConfig, '@common/controlPanel.vue');
export { controlPanelConfig, controlPanelCmp };
export default controlPanelCmp;
</script>

<style scoped>
    .control-panel {
        padding: 10px;
    }
    .cp-horizontal .cp-control-container .control {
        padding-right: 15px;
    }
    .control-panel.cp-horizontal {
        display: flex;
    }
    .control-panel.cp-right {
        flex-direction: row-reverse;
    }
    .control-panel.cp-center {
        justify-content: center;
    }
    .control-panel-actions {
        display: flex;
    }
    .control-panel-actions :deep(.control) {
        margin-left: auto;
    }
</style>
